import { getCellElement } from './generalizationHelpers';
import { mappingMeta } from '../types';
import { getDvFormTemplate } from './clientHelpers';
import { exportToDoc } from './convertFileType';

type RejectionNoteEntry = {
  mappingId: number;
  notes?: string;
  suggestedChange?: string;
  change?: string;
  sourceDocNumber?: string;
};

export const getMappingNotes = (rejectionNotes: RejectionNoteEntry[] = [], suggestionId: number) =>
  rejectionNotes.find((note) => note.mappingId === suggestionId) || {};

export const getTableTitle = (node: Element | null): string | undefined => {
  const table = node?.closest('table');
  if (!table) {
    return undefined;
  }
  const findTitle = (element: HTMLElement | null): string | undefined => {
    return element?.innerText?.includes('Table') ? element.innerText : undefined;
  };
  let currentElement: HTMLElement | null = table;
  while (currentElement && currentElement !== document.body) {
    const title = findTitle(currentElement.previousElementSibling as HTMLElement);
    if (title) {
      return title;
    }
    currentElement = currentElement.parentElement;
  }
  return undefined;
};

export const findTableNumberByTdNodeId = (tdNodeId: string, doc: Document): number => {
  const fileElement = doc.querySelector(`[data-nodeId='${tdNodeId}']`);
  const tableTitle = getTableTitle(fileElement);
  const regex = /Table\s+(\d+)/;

  // First try to get the table number from current table title
  if (tableTitle) {
    const match = tableTitle.match(regex);
    if (match && match[1]) {
      return parseInt(match[1]);
    }
  }

  // If no table title or no number found, look for previous table number
  const currentTable = fileElement?.closest('table');
  if (currentTable) {
    // Get all tables in the document
    const allTables = Array.from(doc.querySelectorAll('table'));
    const currentTableIndex = allTables.indexOf(currentTable);

    // Look through previous tables to find the highest table number
    let highestPreviousTableNumber = 0;
    for (let i = 0; i < currentTableIndex; i++) {
      const previousTableTitle = getTableTitle(allTables[i]);
      if (previousTableTitle) {
        const match = previousTableTitle.match(regex);
        if (match && match[1]) {
          const tableNumber = parseInt(match[1]);
          if (tableNumber > highestPreviousTableNumber) {
            highestPreviousTableNumber = tableNumber;
          }
        }
      }
    }

    // Return the incremented previous table number (or 1 if no previous tables found)
    return highestPreviousTableNumber + 1;
  }

  return 0;
};

export const findPageNumberByNodeId = (tdNodeId: string, doc: Document): number => {
  const element = doc.querySelector(`[data-nodeId='${tdNodeId}']`);

  if (!element) {
    return 0;
  }

  // Function to recursively search through all nodes below the current element
  const searchForPageNumber = (node: Node): number => {
    // Check if current node has text content with page pattern
    if (node.nodeType === Node.TEXT_NODE || node.nodeType === Node.ELEMENT_NODE) {
      const textContent = node.textContent || '';
      // Look for pattern like "Page X of Y" (case insensitive)
      const pageMatch = textContent.match(/(page|pagine|paige)\s+(\d+)\s+of/i);
      if (pageMatch && pageMatch[2]) {
        return parseInt(pageMatch[2], 10);
      }
    }

    // Recursively search child nodes
    for (let i = 0; i < node.childNodes.length; i++) {
      const result = searchForPageNumber(node.childNodes[i]);
      if (result > 0) {
        return result;
      }
    }

    return 0;
  };

  // Start searching from the current element and all its following siblings
  let currentNode: Node | null = element;

  // First search within the current element and its descendants
  const pageNumber = searchForPageNumber(currentNode);
  if (pageNumber > 0) {
    return pageNumber;
  }

  // Then search through following siblings and their descendants
  while (currentNode && currentNode.nextSibling) {
    currentNode = currentNode.nextSibling;
    const result = searchForPageNumber(currentNode);
    if (result > 0) {
      return result;
    }
  }

  // If not found in siblings, search in parent's following siblings
  let parentNode = element.parentNode;
  while (parentNode && parentNode !== doc.body) {
    let siblingNode = parentNode.nextSibling;
    while (siblingNode) {
      const result = searchForPageNumber(siblingNode);
      if (result > 0) {
        return result;
      }
      siblingNode = siblingNode.nextSibling;
    }
    parentNode = parentNode.parentNode;
  }

  return 0;
};

export const constructSuggestionLocation = (
  doc: Document,
  nodeId: string,
): { tableNumber: number; pageNumber: number; tableColumn: number; tableRowNumber: number } => {
  const fileElement = doc.querySelector(`[data-nodeId='${nodeId}']`);
  let targetCell: HTMLTableCellElement | null = null;
  if (fileElement) targetCell = getCellElement(fileElement, doc.body);
  const tableNumber = findTableNumberByTdNodeId(nodeId, doc);
  const pageNumber = findPageNumberByNodeId(nodeId, doc);

  const tableRow: HTMLElement | null = targetCell ? targetCell.parentElement : null;
  const tableColumn: number = targetCell ? targetCell.cellIndex + 1 : 0;
  let tableRowNumber = 0;

  if (tableRow && (tableRow as HTMLTableRowElement)?.rowIndex >= 0) {
    tableRowNumber = (tableRow as HTMLTableRowElement).rowIndex + 1;
  }

  const data = {
    tableNumber,
    pageNumber,
    tableColumn,
    tableRowNumber,
  };

  return data;
};

export const downloadHTML = async (
  mappingMeta: mappingMeta[],
  mappingSessionTitle: string,
  sessionId: number,
  firstName: string,
  lastName: string,
  exportCallback: (formData: FormData) => Promise<unknown>,
) => {
  const htmlContent = `<!DOCTYPE html>
<html>
    <head>
      <meta charset="UTF-8" />
      <style>
        table {
          width: 100%;
          border-collapse: collapse;
          margin: 20px 0;
        }
        th, td {
          border: 1px solid black;
          padding: 8px;
          text-align: left;
        }
        th {
          background-color: #177ba6;
          color: #ffffff;
          font-weight: 800;
          letter-spacing: 0.02em;
        }
      </style>
    </head>
    <body>
    <p><----- Page Break - Landscape -----></p>
      <table>
        <tbody>
          <tr>
            <td>Receiving Document Type</td>
            <td colspan="5"></td>
          </tr>
          <tr>
            <td>Document Title</td>
            <td colspan="5">${mappingSessionTitle}</td>
          </tr>
          <tr>
            <td>Veeva record number and version</td>
            <td colspan="5"></td>
          </tr>
          <tr>
            <td>Source Document Document number and version</td>
            <td colspan="2">Source Document Data Elements Used (e.g., table, figure, section, pages)</td>
            <td>Receiving Document Where Source Used (e.g., table, figure, section, pages)</td>
            <td colspan="2">Data Verification Findings: Description of any discrepancies and justification of acceptability (e.g., "all values comply", "significant figures aligned to product specification", "narrative confirmed by SME")</td>
          </tr>
          ${mappingMeta
            .map((file) => {
              const targetLocations = file.targetLocation?.split('\n').filter(Boolean) || [];
              return targetLocations.map((target) => ({
                veevaInfo: `${file.veevaNumber || ''} ${file.veevaVersion || ''}`,
                sourceInfo: file.sourceLocation || '',
                target: target || '',
              }));
            })
            .flat()
            .sort((a, b) => {
              // Extract numbers from table references for proper numeric sorting
              const getTableNumber = (str: string) => {
                const match = str.match(/Table\s*(\d+)/i);
                return match ? parseInt(match[1]) : -1;
              };
              const aNum = getTableNumber(a.target);
              const bNum = getTableNumber(b.target);
              return aNum - bNum;
            })
            .map(
              (row) => `
            <tr>
              <td>${row.veevaInfo}</td>
              <td colspan="2">${row.sourceInfo}</td>
              <td>${row.target}</td>
              <td colspan="2"></td>
            </tr>
          `,
            )
            .join('')}
          <tr>
            <td>DV Performed By/Date:</td>
            <td>Name:</td>
            <td colspan="2">${firstName || ''} ${lastName || ''}</td>
            <td>Date:</td>
            <td>${new Date().toLocaleDateString()}</td>
          </tr>
        </tbody>
      </table>
    </body>
</html>`;

  try {
    const formData = new FormData();

    // Create HTML blob with proper encoding
    const htmlBlob = new Blob([htmlContent], { type: 'text/html' });
    const htmlFile = new File([htmlBlob], 'htmlFile.html', { type: 'text/html' });
    formData.append('htmlFile', htmlFile);

    // Fetch the template file
    try {
      const dvFormTemplateResponse = await fetch(getDvFormTemplate(), { cache: 'no-store' });
      if (!dvFormTemplateResponse.ok) {
        throw new Error('Failed to fetch template file');
      }

      const templateArrayBuffer = await dvFormTemplateResponse.arrayBuffer();

      // Create template blob with actual file content
      const templateBlob = new Blob([templateArrayBuffer], {
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      });
      const templateFile = new File([templateBlob], 'template.docx', {
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      });

      formData.append('templateFile', templateFile);
    } catch (error) {
      console.error('Error loading template file:', error);
      throw new Error('Failed to load template file');
    }

    // Call export callback with FormData
    const response = (await exportCallback(formData)) as { data: Blob };

    // Handle the downloaded file
    const docxBlob = response.data;
    const downloadUrl = URL.createObjectURL(docxBlob);

    // Create download link and trigger download
    const downloadLink = document.createElement('a');
    downloadLink.href = downloadUrl;
    downloadLink.download = `DV Form For ${mappingSessionTitle}.docx`;
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
    URL.revokeObjectURL(downloadUrl);
  } catch (error) {
    console.error('Error processing DV form:', error);
    exportToDoc(htmlContent, `DV Form For ${mappingSessionTitle}`);
  }
};
